import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { ProgressBar } from '../components/ProgressBar';
import { useSettings, formatTodayLong, getGreeting } from '../lib/settings';
import { useDailyHabits } from '../lib/habitState';
import { useProjectTime, formatDuration } from '../lib/projectTime';
import { loadEntries, JournalEntry } from '../lib/journal';
function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(base: Date, delta: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + delta);
  return d;
}
function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}
function pct(numerator?: number, denominator?: number): string {
  if (numerator === undefined || denominator === undefined || denominator === 0) {
    return '—';
  }
  return `${Math.round(numerator / denominator * 100)}%`;
}
function buildSummary(entry?: JournalEntry): React.ReactNode {
  if (!entry) return <span className="text-gray-300" data-id="element-45">No entry yet</span>;
  const winCount = (entry.wins ?? []).filter(w => w && w.trim().length > 0).length;
  const winsLabel = winCount > 0 ? `${winCount} Win${winCount === 1 ? '' : 's'}` : '';
  const blocker = entry.blocker?.trim() ?? '';
  const tweak = entry.tomorrowsTweak?.trim() ?? '';
  const parts: string[] = [];
  if (winsLabel) parts.push(winsLabel);
  if (blocker) parts.push(blocker);
  const head = parts.join(' • ');
  if (head && tweak) return `${head} → ${tweak}`;
  if (head) return head;
  if (tweak) return `→ ${tweak}`;
  return <span className="text-gray-300" data-id="element-46">No reflection saved</span>;
}
export function JournalFeed() {
  const navigate = useNavigate();
  const settings = useSettings();
  const {
    completedCount,
    totalCount
  } = useDailyHabits();
  const {
    minutesLogged
  } = useProjectTime();
  const today = useMemo(() => new Date(), []);
  const greeting = getGreeting(today);
  const formattedDate = formatTodayLong(today);
  const hpPercentage = totalCount === 0 ? 0 : Math.round(completedCount / totalCount * 100);
  const goalMinutes = settings.ppHours * 60 + settings.ppMinutes;
  const ppPercentage = goalMinutes === 0 ? 0 : Math.round(minutesLogged / goalMinutes * 100);
  // Pull saved journal entries
  const entries = useMemo(() => loadEntries(), []);
  // Yesterday's tomorrow's-tweak — becomes today's improvement focus
  const yesterdayEntry = entries[dateKey(addDays(today, -1))];
  const yesterdaysTweak = yesterdayEntry?.tomorrowsTweak?.trim();
  // Last 3 days (excluding today)
  const recentDays = [1, 2, 3].map(offset => {
    const date = addDays(today, -offset);
    const key = dateKey(date);
    return {
      key,
      label: formatShortDate(date),
      entry: entries[key]
    };
  });
  return <div className="max-w-5xl mx-auto p-8" data-id="element-47">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-48">
        {/* Left Column: Greeting & Today's Overview */}
        <div className="lg:col-span-7 space-y-10" data-id="element-49">
          <div data-id="element-50">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-51">
              {greeting}, {settings.name}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-52">{formattedDate}</p>
          </div>

          <div className="space-y-6" data-id="element-53">
            <p className="text-sm font-semibold text-gray-900" data-id="element-54">
              Goal:{' '}
              <span className="font-semibold" data-id="element-55">
                {settings.goal || <span className="text-gray-400 font-normal italic" data-id="element-56">
                    No goal set — add one in GPS Settings
                  </span>}
              </span>
            </p>
            <p className="text-sm font-semibold text-gray-900" data-id="element-57">
              90-Day Challenge:{' '}
              <span className="font-semibold" data-id="element-58">
                {settings.ninetyDayChallenge || <span className="text-gray-400 font-normal italic" data-id="element-59">
                    Set your challenge in GPS Settings
                  </span>}
              </span>
            </p>
            <p className="text-sm font-semibold text-gray-900" data-id="element-60">
              30-Day Experiment:{' '}
              <span className="font-semibold" data-id="element-61">
                {settings.thirtyDayExperiment || <span className="text-gray-400 font-normal italic" data-id="element-62">
                    Set your experiment in GPS Settings
                  </span>}
              </span>
            </p>
            <ProgressBar label="HP: Habit Progress" value={`${completedCount}/${totalCount} habits done`} percentage={hpPercentage} colorClass="bg-emerald-500" flipped data-id="element-63" />
            <ProgressBar label="PP: Project Progress" value={`${formatDuration(minutesLogged)} / ${formatDuration(goalMinutes)}`} percentage={Math.min(ppPercentage, 100)} colorClass="bg-indigo-500" flipped data-id="element-64" />
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-65">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-66">
              Yesterday's Reflection / Today's Improvement:
            </h3>
            {yesterdaysTweak ? <p className="text-gray-800 text-lg font-medium leading-relaxed" data-id="element-67">
                "{yesterdaysTweak}"
              </p> : <p className="text-gray-300 text-lg font-medium leading-relaxed italic" data-id="element-68">
                No reflection from yesterday yet.
              </p>}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-69">
            <button onClick={() => navigate('/log')} className="px-8 py-3.5 bg-gray-400 hover:bg-gray-500 text-white font-semibold rounded-xl shadow-sm transition-colors" data-id="element-70">
              Log Progress
            </button>
            <button onClick={() => navigate('/reflection')} className="px-8 py-3.5 bg-gray-400 hover:bg-gray-500 text-white font-semibold rounded-xl shadow-sm transition-colors" data-id="element-71">
              Log Reflection
            </button>
          </div>
        </div>

        {/* Right Column: Recent Days & Search */}
        <div className="lg:col-span-5 space-y-6" data-id="element-72">
          <div className="flex items-center justify-between mb-4" data-id="element-73">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-74">
              Recent GPS Days
            </h2>
            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-75">
              <Filter className="w-5 h-5" data-id="element-76" />
            </button>
          </div>

          <div className="space-y-4" data-id="element-77">
            {recentDays.map(day => <div key={day.key} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-78">
                <div className="flex justify-between items-center mb-3" data-id="element-79">
                  <span className="font-semibold text-gray-900" data-id="element-80">
                    {day.label}
                  </span>
                  <div className="text-xs font-medium text-gray-500 flex gap-3" data-id="element-81">
                    <span data-id="element-82">
                      HP {pct(day.entry?.hpCompleted, day.entry?.hpTotal)}
                    </span>
                    <span data-id="element-83">
                      PP {pct(day.entry?.ppCompleted, day.entry?.ppTotal)}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-84">
                  {buildSummary(day.entry)}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}